import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  BarChart3,
  TrendingUp,
  MessageSquare,
  FileText,
  Award,
  Calendar,
  Loader,
} from "lucide-react";
import communityService from "../../../services/communityService";
import StatCard from "./StatCard";
import LeaderboardTable from "./LeaderboardTable";
import "./AnalyticsDashboard.css";

const AnalyticsDashboard = ({ communityId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (communityId) {
      loadStats();
    }
  }, [communityId]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await communityService.getMyStats(communityId);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
toast.error("Failed to load your stats");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <div className="analytics-title">
          <BarChart3 size={24} />
          <h2>Analytics & Leaderboard</h2>
        </div>
        <p className="analytics-subtitle">
          Track your performance and see how you rank in the community
        </p>
      </div>

      {/* Your Stats Section */}
      <div className="analytics-section">
        <h3>Your Stats</h3>
        {loading ? (
          <div className="analytics-loading">
            <Loader className="spinner" size={32} />
            <p>Loading your stats...</p>
          </div>
        ) : stats ? (
          <div className="stats-grid">
            <StatCard
              icon={FileText}
              label="Threads Created"
              value={stats.threadsCreated || 0}
              subtext="Your contributions"
              color="#2196f3"
            />
            <StatCard
              icon={MessageSquare}
              label="Replies Posted"
              value={stats.repliesPosted || 0}
              subtext="Your engagement"
              color="#4caf50"
            />
            <StatCard
              icon={TrendingUp}
              label="Total Upvotes"
              value={stats.upvotesReceived || stats.totalUpvotes || 0}
              subtext="Votes received"
              color="#ff9800"
            />
            <StatCard
              icon={Award}
              label="Awards Received"
              value={
                stats.reactionsReceived
                  ? Object.values(stats.reactionsReceived).reduce((a, b) => a + b, 0)
                  : stats.awardsReceived || 0
              }
              subtext="Special recognition"
              color="#9c27b0"
            />
          </div>
        ) : (
          <div className="stats-empty">
            <p>No stats available yet. Start contributing to the community!</p>
          </div>
        )}

        {stats && (
          <div className="stats-additional-info">
            <div className="additional-info-item">
              <Calendar size={16} />
              <span>
                <strong>Joined:</strong> {formatDate(stats.joinedDate || stats.joinedAt)}
              </span>
            </div>
            {stats.rank && (
              <div className="additional-info-item">
                <TrendingUp size={16} />
                <span>
                  <strong>Current Rank:</strong> #{stats.rank}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Leaderboard Section */}
      <div className="analytics-section">
        <LeaderboardTable communityId={communityId} />
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
